import { t } from '@procumi/shared';
import type { RangeKind } from '@procumi/shared';
import { useEffect, useRef, useState } from 'react';
import { DateInput } from '../DateInput';
import { openSearch } from '../CommandSearch';
import { I } from './shared';

export const RANGE_TEXT: Record<RangeKind, string> = {
  month: t('Luna aceasta'),
  '30d': t('Ultimele 30 de zile'),
  '90d': t('Ultimele 90 de zile'),
  year: t('Anul acesta'),
  custom: t('Perioadă aleasă'),
};

const greeting = (h: number) => (h < 11 ? t('Bună dimineața') : h < 18 ? t('Bună ziua') : t('Bună seara'));

/**
 * Capul panoului: salutul, căutarea (Ctrl K), șantierul, perioada și „Cerere
 * nouă”. Perioada și șantierul schimbă toate cifrele de dedesubt.
 */
export function DashboardHeader({
  name,
  facts,
  sites,
  site,
  onSite,
  range,
  custom,
  onRange,
  onNew,
  onHelp,
}: {
  name: string;
  facts: string;
  sites: { id: string; name: string }[];
  site: string;
  onSite: (id: string) => void;
  range: RangeKind;
  custom: { from: string; to: string };
  onRange: (k: RangeKind, custom?: { from: string; to: string }) => void;
  onNew: () => void;
  onHelp: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(custom);
  const box = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => {
      if (!box.current?.contains(e.target as Node)) setOpen(false);
    };
    const esc = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', esc);
    return () => {
      document.removeEventListener('mousedown', close);
      document.removeEventListener('keydown', esc);
    };
  }, [open]);
  const first = name.split(' ')[0] ?? name;
  const mac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform);

  return (
    <div className="db-top">
      <div className="db-greet">
        <h1>
          {greeting(new Date().getHours())}, {first}
        </h1>
        <p>{facts}</p>
      </div>
      <div className="db-tools">
        <button type="button" className="db-sel db-help" onClick={onHelp}>
          <I d="help" /> {t('Centru de ajutor')}
        </button>
        {/* pe telefon bara laterală n-are căutare: rămâne aici */}
        <button type="button" className="db-search" onClick={openSearch}>
          <I d="search" />
          <span>{t('Caută cereri, furnizori, comenzi…')}</span>
          <kbd>{mac ? '⌘' : t('Ctrl')} K</kbd>
        </button>
        {sites.length > 1 ? (
          <label className="db-sel">
            <I d="site" />
            <select aria-label={t('Șantier')} value={site} onChange={(e) => onSite(e.target.value)}>
              <option value="">{t('Toate șantierele')}</option>
              {sites.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
            <I d="down" size={14} />
          </label>
        ) : null}
        <div className="db-range" ref={box}>
          <button
            type="button"
            className="db-sel"
            aria-haspopup="menu"
            aria-expanded={open}
            onClick={() => setOpen(!open)}
          >
            <I d="calendar" />
            <span>{RANGE_TEXT[range]}</span>
            <I d="down" size={14} />
          </button>
          {open ? (
            <div className="db-range-pop" role="menu">
              {(['month', '30d', '90d', 'year'] as RangeKind[]).map((k) => (
                <button
                  key={k}
                  type="button"
                  role="menuitemradio"
                  aria-checked={range === k}
                  onClick={() => {
                    onRange(k);
                    setOpen(false);
                  }}
                >
                  {RANGE_TEXT[k]}
                  {range === k ? <I d="check" size={14} /> : null}
                </button>
              ))}
              <div className="db-range-c">
                <span>{t('Perioadă aleasă')}</span>
                <div>
                  <DateInput
                    value={draft.from}
                    max={draft.to || undefined}
                    onChange={(v) => setDraft({ ...draft, from: v })}
                  />
                  <DateInput
                    value={draft.to}
                    min={draft.from || undefined}
                    onChange={(v) => setDraft({ ...draft, to: v })}
                  />
                </div>
                <button
                  type="button"
                  className="btn small primary"
                  disabled={!draft.from || !draft.to}
                  onClick={() => {
                    onRange('custom', draft);
                    setOpen(false);
                  }}
                >
                  {t('Aplică')}
                </button>
              </div>
            </div>
          ) : null}
        </div>
        <button type="button" className="btn primary db-new" onClick={onNew}>
          <I d="plus" /> {t('Cerere nouă')}
        </button>
      </div>
    </div>
  );
}
